import type * as me from '../me'

import type { Feature } from './feature'
import * as models from './models'
import type { State } from './state'

/** TODO */
export class Selectors {
  /** @see {@link State.isUploading} */
  public readonly isUploading
  /** @see {@link State.uploadError} */
  public readonly hasUploadError
  /** @see {@link State.isDownloading} */
  public readonly isDownloading
  /** @see {@link State.downloadError} */
  public readonly hasDownloadError
  /** @see {@link State.finder.isOpen} */
  public readonly finderIsOpen
  /** @see {@link State.finder.files} */
  public readonly finderFiles
  /** Prepared hierarchy for File Finder presentation. */
  public readonly fileTree
  /** @see {@link State.finder.filesRequest.isLoading} */
  public readonly isFinderFilesLoading
  /** @see {@link State.finder.filesRequest.error} */
  public readonly finderFilesError

  // NOTE: Type will be inferred from feature when instantiated
  constructor(feature: Feature, meFeature: me.Feature) {
    const uploadLookup = feature.state.project(state => state.uploadLookup)
    const downloadLookup = feature.state.project(state => state.downloadLookup)

    const finder = feature.state.project(state => state.finder)
    this.finderIsOpen = finder.project(state => state.isOpen)
    this.finderFiles = finder.project(state => state.files)
    this.isFinderFilesLoading = finder.project(
      state => state.filesRequest.isLoading,
    )
    this.finderFilesError = finder.project(state => state.filesRequest.error)
    this.fileTree = this.finderFiles.project(
      meFeature.contexts.selectors.availableApps.asSelector,
      (files, apps) => models.FileTree.from(files, apps),
    )

    const _uploadEntry = feature.createSelectorMethod(
      (request: models.FileRequest.ForUpload) =>
        uploadLookup.project(
          lookup => lookup[models.FileRequest.getKey(request)],
        ).asSelector,
    )
    const _downloadEntry = feature.createSelectorMethod(
      (request: models.FileRequest.ForDownload) =>
        downloadLookup.project(
          lookup => lookup[models.FileRequest.getKey(request)],
        ).asSelector,
    )

    this.isUploading = feature.createSelectorMethod(
      (fileInfo: models.FileRequest.ForUpload) =>
        _uploadEntry(fileInfo).project(entry => !!entry?.isUploading)
          .asSelector,
    )

    this.hasUploadError = feature.createSelectorMethod(
      (fileInfo: models.FileRequest.ForUpload) =>
        _uploadEntry(fileInfo).project(entry => {
          const error = entry?.error
          if (error === undefined) {
            return undefined
          }
          if (typeof error === 'string') {
            return error
          }
          return error.message
        }).asSelector,
    )

    this.hasDownloadError = feature.createSelectorMethod(
      (fileInfo: models.FileRequest.ForDownload) =>
        _downloadEntry(fileInfo).project(entry => {
          const error = entry?.error
          if (error === undefined) {
            return undefined
          }
          if (typeof error === 'string') {
            return error
          }
          return error.message
        }).asSelector,
    )

    this.isDownloading = feature.createSelectorMethod(
      (fileInfo: models.FileRequest.ForDownload) =>
        _downloadEntry(fileInfo).project(entry => !!entry?.isDownloading)
          .asSelector,
    )
  }
}
